"use client";
import Icon from "@/components/icons/Icons";
import React, { useMemo, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { SchemeWithCategory } from "@/type/overlapType";
import { normalizeRiskCategory } from "@/utils/commonFunctions";
import FundSearchBarCompare from "../ui/FundSearchBarCompare";

type RiskCategory = "very low" | "low" | "medium" | "moderate to high" | "high" | "very high";

export default function AddFundsForCompare({ funds }: { funds?: SchemeWithCategory[] }) {
  const router = useRouter();
  const [openSearch, setOpenSearch] = useState(false);
  const [isPending, startTransition] = useTransition();

  const searchBarRef = React.useRef<HTMLDivElement>(null);

  const slug = useMemo(() => {
    if (!funds || funds.length === 0) return null;

    const urls = funds.map((fund) => fund.url).filter((url): url is string => Boolean(url));

    if (urls.length === 0) return null;

    return urls.join("-vs-");
  }, [funds]);

  const fundMap = useMemo(() => {
    const map = new Map<string, SchemeWithCategory>();

    for (const fund of funds ?? []) {
      map.set(fund.url, fund);
    }

    return map;
  }, [funds]);

  const selectedFunds = useMemo(() => {
    if (!slug) return [];

    return slug
      .split("-vs-")
      .map((url) => fundMap.get(url))
      .filter((f): f is SchemeWithCategory => Boolean(f));
  }, [slug, fundMap]);

  const removeFund = (removeUrl: string) => {
    if (!slug) return;

    const nextSlug = slug
      .split("-vs-")
      .filter((u) => u !== removeUrl)
      .join("-vs-");

    startTransition(() => {
      router.push(nextSlug ? `/mutual-fund/compare/${nextSlug}` : "/mutual-fund/compare");
    });
  };

  const scrollToSearchIfNeeded = () => {
    const el = searchBarRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    const distanceFromTop = rect.top;

    if (Math.abs(distanceFromTop) > 180) return;

    window.scrollTo({
      top: window.scrollY + distanceFromTop - 100,
      behavior: "smooth",
    });
  };

  const riskColorMap: Record<RiskCategory, string> = {
    "very low": "before:bg-[#30AD43] text-[#30AD43]",
    low: "before:bg-[#5ECB3B] text-[#5ECB3B]",
    medium: "before:bg-[#F5C02E] text-[#F5C02E]",
    "moderate to high": "before:bg-[#FF8B3D] text-[#FF8B3D]",
    high: "before:bg-[#F95B4E] text-[#F95B4E]",
    "very high": "before:bg-[#E43E3E] text-[#E43E3E]",
  };

  return (
    <>
      <div className="section relative space-y-2 mb-8">
        <div ref={searchBarRef}>
          <FundSearchBarCompare isOpen={openSearch} onOpen={() => setOpenSearch(true)} onClose={() => setOpenSearch(false)} slug={slug ?? ""} isPendingParent={isPending} />
        </div>

        <div className="flex items-center justify-center gap-2.5 text-white text-xs font-light">
          <span className="inline-flex items-center gap-2.5 after:content-[''] after:inline-block after:w-1 after:h-1 after:rounded-full after:bg-white">Compare Up to 4 Funds</span>
          <span>Free Analysis</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 bg-white/50 md:rounded-6xl overflow-hidden border-y md:border border-white p-3 md:p-4">
        {Array.from({ length: 4 }).map((_, index) => {
          const fund = selectedFunds[index] ?? null;
          const risk = normalizeRiskCategory(fund?.scheme_risk || "medium");

          const key = fund ? `fund-${fund.id}` : `empty-${index}`;

          if (!fund) {
            return (
              <button
                key={key}
                onClick={() => {
                  scrollToSearchIfNeeded();
                  setOpenSearch(true);
                }}
                className="h-29.5 bg-white rounded-2xl group flex flex-col gap-2 items-center justify-center p-4 text-primaryBlue border border-dashed border-[#8AA6D5] transition duration-300 hover:border-[#376abd] hover:bg-[#f2f7ff]"
              >
                <span className="w-9 h-9 bg-primaryBlue/5 inline-flex items-center justify-center rounded-full">
                  <Icon.expandableIcon />
                </span>
                Add Fund
              </button>
            );
          }

          return (
            <div key={key} title={fund.scheme_name} className="h-29.5 relative bg-white rounded-2xl flex flex-col items-start justify-center p-3 md:p-4 border border-dashed border-[#8AA6D5]">
              <div className="h-10 sm:h-12 text-sm sm:text-base font-semibold line-clamp-2 pr-3">{fund.scheme_name}</div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 md:gap-2.5 text-xs leading-1.3 mt-auto font-medium">
                <span className="text-[#687D9F] whitespace-nowrap">{fund.fund_category}</span>
                <span className={`inline-flex items-center gap-1.5 before:content-[''] before:inline-block before:shrink-0 before:w-1.5 before:h-1.5 before:rounded-full ${riskColorMap[(risk || "medium") as RiskCategory]}`}>
                  <span className="line-clamp-1 capitalize">{risk} Risk</span>
                </span>
              </div>

              {/* Remove */}
              <button onClick={() => removeFund(fund.url)} className="absolute top-1.5 right-1.5 w-5 h-5 p-1 hover:text-primaryBlue">
                <Icon.crossIcon className="w-full h-full" />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
